import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { cn } from "@bowfast/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { CheckCheckIcon } from "lucide-react";
import { fadeUp } from "~/lib/animations";
import { getSignalConfig } from "~/pages/notifications/notifications.config";
import type { Signal } from "~/pages/notifications/notifications.types";
import { formatTime } from "~/pages/notifications/notifications.utils";

interface SignalItemProps {
  signal: Signal;
  onMarkRead?: (id: string) => void;
}

export function SignalItem({ signal, onMarkRead }: Readonly<SignalItemProps>) {
  const cfg = getSignalConfig(signal.type);
  const Icon = cfg.Icon;
  const isUnread = !signal.readAt;
  const now = new Date();

  return (
    <motion.div variants={fadeUp}>
      <div
        className={cn(
          "group relative flex gap-4 px-5 py-4 transition-colors",
          "border-b border-border last:border-b-0",
          isUnread && "bg-abyss-50/30 dark:bg-abyss-900/20",
          "hover:bg-muted/40",
        )}
      >
        {/* Unread accent bar */}
        {isUnread && <div className={cn("absolute inset-y-0 left-0 w-0.75 rounded-r-sm", cfg.accentBar)} />}

        {/* Type icon */}
        <div className={cn("mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg", cfg.iconBg)}>
          <Icon className={cn("size-4", cfg.iconColor)} />
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p
              className={cn(
                "text-sm leading-snug",
                isUnread ? "font-semibold text-foreground" : "font-medium text-foreground/80",
              )}
            >
              {signal.title}
            </p>
            <span className="shrink-0 text-[0.6875rem] text-muted-foreground">{formatTime(signal.createdAt, now)}</span>
          </div>
          <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-muted-foreground">{signal.body}</p>

          {/* Tags + Actions */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-[0.625rem] font-semibold uppercase tracking-wide">
              {cfg.label}
            </Badge>
            {signal.data?.expeditionSlug && (
              <Link
                to="/expeditions/$expeditionSlug"
                params={{ expeditionSlug: signal.data.expeditionSlug }}
                className="text-[0.6875rem] font-medium text-abyss-500 hover:underline"
              >
                View expedition
              </Link>
            )}

            {signal.actionable && signal.type === "crew_request" && (
              <div className="ml-auto flex gap-1.5">
                <Button size="sm" className="h-6 px-2.5 text-xs">
                  Accept
                </Button>
                <Button variant="outline" size="sm" className="h-6 px-2.5 text-xs">
                  Decline
                </Button>
              </div>
            )}
            {signal.actionable && signal.type === "payment_failed" && (
              <div className="ml-auto">
                <Button variant="outline" size="sm" className="h-6 px-2.5 text-xs">
                  Retry
                </Button>
              </div>
            )}
            {signal.actionable && signal.type === "cert_expiring" && (
              <div className="ml-auto">
                <Button variant="outline" size="sm" className="h-6 px-2.5 text-xs">
                  Send reminder
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Mark read (hover) */}
        {isUnread && (
          <button
            type="button"
            className="mt-1 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
            onClick={() => onMarkRead?.(signal.id)}
            aria-label="Mark as read"
          >
            <CheckCheckIcon className="size-3.5" />
          </button>
        )}
      </div>
    </motion.div>
  );
}
